'use client'

import { noop } from 'lodash'
import { createContext, ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react'

import { ItemUploadContextType } from '../types/context'

export const ItemUploadContext = createContext<ItemUploadContextType>({
  openPhotoInput: noop,
  fileInputRef: { current: null },
  isFormDisabled: false,
  enableForm: noop,
  disableForm: noop,
})

export const ItemUploadContextProvider = ({ children }: { children: ReactNode }) => {
  const [isFormDisabled, setIsFormDisabled] = useState(false)

  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleAddPhotosClick = useCallback(() => {
    const { current } = fileInputRef

    if (!current) return

    current.click()
    current.focus()
  }, [])

  const disableForm = useCallback(() => setIsFormDisabled(true), [])
  const enableForm = useCallback(() => setIsFormDisabled(false), [])

  const providerValue = useMemo(
    () => ({
      openPhotoInput: handleAddPhotosClick,
      fileInputRef,
      isFormDisabled,
      enableForm,
      disableForm,
    }),
    [handleAddPhotosClick, isFormDisabled, enableForm, disableForm],
  )

  return <ItemUploadContext.Provider value={providerValue}>{children}</ItemUploadContext.Provider>
}

export const useItemUploadContext = () => {
  const context = useContext(ItemUploadContext)

  if (!context) {
    throw new Error('useItemUploadContext must be used within a ItemUploadContext')
  }

  return context
}
